import React, {useMemo, useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import {Redirect, useRouteMatch} from 'react-router-dom';
import styled from 'styled-components';
import {Detail} from '@forecast-it/design-system';
import MissionControlHeader from '../type-script-files/MissionControlHeader';
import {CompanyFeatureFlagsModal} from '../company_feature_flags_modal';
import Util from '../../../forecast-app/shared/util/util';
import type {FeatureFlagsPage_viewer$key} from './__generated__/FeatureFlagsPage_viewer.graphql';
import type {MissionControlHeader_viewer$key} from '../type-script-files/__generated__/MissionControlHeader_viewer.graphql';
import {FeatureFlagRow} from './FeatureFlagRow';
import {
	FEATURE_FLAG_GRID_COLUMNS,
	FEATURE_FLAG_GRID_GAP,
	FEATURE_FLAG_GRID_PADDING_LEFT,
	FEATURE_FLAG_GRID_PADDING_RIGHT,
	FEATURE_FLAG_LIST_MIN_WIDTH,
} from './featureFlagGrid';
import {FeatureFlagsToolbar} from './FeatureFlagsToolbar';
import {TeamLegend} from './TeamLegend';
import {FEATURE_FLAG_TEAMS} from './featureFlagTeams';
import {
	ALL_TEAMS,
	buildFeatureFlagRows,
	encodeCompanyId,
	filterFeatureFlagRows,
	getEligibleCompanyIds,
	resolveStoredTeamFilter,
	resolveTeamFilterChange,
	TeamFilterValue,
} from './featureFlagRows';
import {buildCompanyFeatureFlags} from './companyFeatureFlags';
import {buildFeatureFlagDetail, findDetailFeature} from './featureFlagDetailData';
import {FeatureFlagDetail} from './FeatureFlagDetail';
import {buildRolloutDirectory} from './rollout/featureFlagRolloutDirectory';

// Shared with the existing feature flags page so switching between the two keeps your filters.
// The team key's spelling is the one already in localStorage — changing it would silently orphan it.
const TEAM_FILTER_STORAGE_KEY = 'missin-control-featture-flag-team';
const SHOW_ROLLED_OUT_STORAGE_KEY = 'mission-control-feature-flag-showRolledOut';

const PageWrapper = styled.div`
	max-width: 1800px;
	margin: 0 auto;
	padding: 28px 32px 64px;
	overflow-x: auto;
`;

const ColumnHeader = styled.div`
	display: grid;
	min-width: ${FEATURE_FLAG_LIST_MIN_WIDTH};
	grid-template-columns: ${FEATURE_FLAG_GRID_COLUMNS};
	align-items: center;
	gap: ${FEATURE_FLAG_GRID_GAP};
	padding: 0 ${FEATURE_FLAG_GRID_PADDING_RIGHT} 8px ${FEATURE_FLAG_GRID_PADDING_LEFT};
	/* Matches the row's border. Everything is border-box, so a row spends 2px of its width on its
	   own border and lays its columns out inside what is left; a header without the same border
	   would give its columns 2px more to share and sit misaligned against the rows beneath it. */
	border: 1px solid transparent;
`;

// Every header label sits in one of these, so all six share a box model. Wrapping only some of them
// leaves the rest aligned on their text baseline instead, which drops the wrapped ones a couple of
// pixels below their neighbours.
const HeaderCell = styled.span<{$alignRight?: boolean}>`
	display: flex;
	align-items: center;
	justify-content: ${props => (props.$alignRight ? 'flex-end' : 'flex-start')};
	min-width: 0;
`;

const RowList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: ${FEATURE_FLAG_LIST_MIN_WIDTH};
`;

export const featureFlagsPageQuery = graphql`
	query FeatureFlagsPage_Query {
		viewer {
			actualPersonId
			component(name: "control_feature_flags")
			...FeatureFlagsPage_viewer
		}
	}
`;

const featureFlagsPageFragment = graphql`
	fragment FeatureFlagsPage_viewer on Viewer {
		component(name: "control_feature_flags")
		id
		...MissionControlHeader_viewer
		missionControlData {
			id
			companies {
				id
				companyId
				name
				tier
				accessBlocked
			}
			forecastPersons {
				id
				firstName
				lastName
				email
			}
			otherPersons {
				id
				firstName
				lastName
				email
				companyId
			}
			availableFeatures {
				id
				name
				key
				team
				rolledOut
				rolledOutDate
				cleanedUp
				companies {
					id
					companyId
					name
					timestamp
				}
				persons {
					id
					personId
					companyId
					companyName
					name
					email
					timestamp
				}
				personsOptedOut {
					id
					companyId
					name
				}
			}
		}
	}
`;

interface FeatureFlagsPageProps {
	viewer: FeatureFlagsPage_viewer$key;
}

export default function FeatureFlagsPage({viewer}: FeatureFlagsPageProps): JSX.Element | null {
	const data = useFragment(featureFlagsPageFragment, viewer);

	// useRouteMatch with no argument resolves to the route this page is registered under in the
	// router — the flag list path. The route is non-exact, so a trailing flag key already matches it
	// and the detail screen needs no route of its own.
	const {path, url} = useRouteMatch();
	const detailMatch = useRouteMatch<{flagKey: string}>(`${path}/:flagKey`);
	const detailFlagKey = detailMatch?.params.flagKey ?? null;

	const [teamFilter, setTeamFilter] = useState<TeamFilterValue>(() =>
		resolveStoredTeamFilter(Util.localStorageGetItemWithDefault(TEAM_FILTER_STORAGE_KEY, ALL_TEAMS))
	);
	const [showRolledOut, setShowRolledOut] = useState<boolean>(
		() => Util.localStorageGetItemWithDefault(SHOW_ROLLED_OUT_STORAGE_KEY, 'true') === 'true'
	);
	const [checkedCompanyId, setCheckedCompanyId] = useState<string | null>(null);
	// Bumped when the company modal closes, to clear the picker that opened it — see the toolbar's
	// companyPickerResetToken.
	const [companyPickerResetToken, setCompanyPickerResetToken] = useState(0);

	const missionControlData = data?.missionControlData;
	const availableFeatures = missionControlData?.availableFeatures;

	const eligibleCompanyIds = useMemo(() => getEligibleCompanyIds(missionControlData?.companies), [missionControlData]);
	const allRows = useMemo(
		() => buildFeatureFlagRows(availableFeatures, eligibleCompanyIds),
		[availableFeatures, eligibleCompanyIds]
	);
	const visibleRows = useMemo(
		() => filterFeatureFlagRows(allRows, {teamId: teamFilter, showRolledOut}),
		[allRows, teamFilter, showRolledOut]
	);

	const companyOptions = useMemo(
		() =>
			(missionControlData?.companies ?? [])
				.filter(company => company?.companyId != null)
				.map(company => ({
					id: String(company?.companyId),
					name: company?.name ?? '',
				})),
		[missionControlData]
	);

	// Always the same six teams, in the same order as the legend — a fixed list, not one derived from
	// what happens to be loaded.
	const teamFilterOptions = useMemo(
		() => [
			{id: ALL_TEAMS as TeamFilterValue, name: 'All teams'},
			...FEATURE_FLAG_TEAMS.map(team => ({
				id: team.id as TeamFilterValue,
				name: team.label,
			})),
		],
		[]
	);

	const checkedCompanyFeatures = useMemo(
		() => (checkedCompanyId == null ? [] : buildCompanyFeatureFlags(availableFeatures, encodeCompanyId(checkedCompanyId))),
		[availableFeatures, checkedCompanyId]
	);

	// Built once for the page and handed to the detail screen. The two person lists together are the
	// active, non-virtual, non-client population, which is what the rollout modal counts a company's
	// users by and picks individual users from.
	const rolloutDirectory = useMemo(
		() =>
			buildRolloutDirectory(
				missionControlData?.companies,
				missionControlData?.otherPersons,
				missionControlData?.forecastPersons
			),
		[missionControlData]
	);

	const detail = useMemo(() => {
		if (detailFlagKey == null) {
			return null;
		}
		const feature = findDetailFeature(availableFeatures, detailFlagKey);
		return feature == null ? null : buildFeatureFlagDetail(feature, eligibleCompanyIds);
	}, [availableFeatures, detailFlagKey, eligibleCompanyIds]);

	if (!missionControlData) {
		return null;
	}

	// A key no flag has, or a rolled-out flag, has no detail screen to show — go back to the list
	// rather than dead-ending on a bookmarked or hand-typed URL.
	if (detailFlagKey != null && detail == null) {
		return <Redirect to={url} />;
	}

	const handleTeamFilterChange = (value: TeamFilterValue | null): void => {
		const next = resolveTeamFilterChange(value);
		Util.localStorageSetItem(TEAM_FILTER_STORAGE_KEY, next);
		setTeamFilter(next);
	};

	const handleShowRolledOutChange = (value: boolean): void => {
		Util.localStorageSetItem(SHOW_ROLLED_OUT_STORAGE_KEY, String(value));
		setShowRolledOut(value);
	};

	// Assigning to the header's fragment key is the check that keeps `...MissionControlHeader_viewer`
	// in the fragment above: drop the spread and this stops compiling. The cast below cannot do that
	// job — MissionControlHeader's props are typed as its resolved fragment data rather than a key, so
	// handing the container a key needs a cast, and a cast would silently accept anything.
	const headerViewer: MissionControlHeader_viewer$key = data;

	return (
		<div>
			<MissionControlHeader
				viewer={headerViewer as unknown as React.ComponentProps<typeof MissionControlHeader>['viewer']}
			/>
			{detail != null ? (
				<FeatureFlagDetail
					backPath={url}
					detail={detail}
					directory={rolloutDirectory}
					eligibleCompanyIds={eligibleCompanyIds}
				/>
			) : (
				<PageWrapper>
					<FeatureFlagsToolbar
						visibleCount={visibleRows.length}
						totalCount={allRows.length}
						teamFilter={teamFilter}
						onTeamFilterChange={handleTeamFilterChange}
						showRolledOut={showRolledOut}
						onShowRolledOutChange={handleShowRolledOutChange}
						companies={companyOptions}
						onCompanySelected={setCheckedCompanyId}
						teamFilterOptions={teamFilterOptions}
						companyPickerResetToken={companyPickerResetToken}
					/>
					<TeamLegend />
					<ColumnHeader>
						<HeaderCell>
							<Detail>FLAG</Detail>
						</HeaderCell>
						<HeaderCell>
							<Detail>TEAM</Detail>
						</HeaderCell>
						<HeaderCell $alignRight>
							<Detail>COMPANIES</Detail>
						</HeaderCell>
						<HeaderCell $alignRight>
							<Detail>USERS</Detail>
						</HeaderCell>
						<HeaderCell>
							<Detail>ROLLOUT</Detail>
						</HeaderCell>
						<HeaderCell />
					</ColumnHeader>
					<RowList data-cy="feature-flag-list">
						{visibleRows.length === 0 ? (
							<Detail>No feature flags match the current filters.</Detail>
						) : (
							visibleRows.map(row => <FeatureFlagRow key={row.key} row={row} detailPath={`${url}/${row.key}`} />)
						)}
					</RowList>
				</PageWrapper>
			)}
			{checkedCompanyId != null && (
				<CompanyFeatureFlagsModal
					features={checkedCompanyFeatures}
					closeModal={() => {
						setCheckedCompanyId(null);
						setCompanyPickerResetToken(token => token + 1);
					}}
				/>
			)}
		</div>
	);
}
